/** Fired after cookie preferences are saved (detail: CookieConsentPreferences). */
export const COOKIE_CONSENT_UPDATED_EVENT = 'hiromatch:cookie-consent-updated';

type SentryModule = typeof import('@sentry/react');

let sentryInitialized = false;
let sentryModule: SentryModule | null = null;
let sentryLoadPromise: Promise<SentryModule | null> | null = null;

export function isSentryInitialized(): boolean {
  return sentryInitialized;
}

async function loadSentryModule(): Promise<SentryModule | null> {
  if (sentryModule) return sentryModule;
  if (!sentryLoadPromise) {
    sentryLoadPromise = import('@sentry/react').then((mod) => {
      sentryModule = mod;
      return mod;
    });
  }
  return sentryLoadPromise;
}

/**
 * Initialize Sentry only when Analytics cookies are enabled.
 * Uses dynamic import so no Sentry network activity or SDK init occurs before consent.
 */
export async function initSentryIfAnalyticsAllowed(analyticsEnabled: boolean): Promise<void> {
  if (!analyticsEnabled) {
    await teardownSentry();
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn || sentryInitialized) return;

  const Sentry = await loadSentryModule();
  if (!Sentry) return;

  const env = import.meta.env.VITE_SENTRY_ENV ?? (import.meta.env.PROD ? 'production-fe' : 'development-fe');
  const release = import.meta.env.VITE_RELEASE_TAG ?? 'unknown';

  Sentry.init({
    dsn,
    environment: env,
    release,
    integrations: [
      Sentry.browserTracingIntegration({
        tracePropagationTargets: [
          /^https:\/\/api-bmreqabbcq-ey\.a\.run\.app/,
          /^https:\/\/candidateapplication-bmreqabbcq-ey\.a\.run\.app/,
          /^https:\/\/europe-west3-recruiterai-8d65b\.cloudfunctions\.net/,
        ],
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
  });

  sentryInitialized = true;
}

/** Stop sending events when Analytics consent is revoked (no page reload required). */
export async function teardownSentry(): Promise<void> {
  if (!sentryInitialized) return;
  const Sentry = sentryModule ?? (await loadSentryModule());
  if (Sentry) {
    await Sentry.close();
  }
  sentryInitialized = false;
}

export function setSentryUser(user: { id: string; email?: string; username?: string } | null): void {
  if (!sentryInitialized || !sentryModule) return;
  if (user) {
    sentryModule.setUser(user);
  } else {
    sentryModule.setUser(null);
  }
}

export function setSentryTag(key: string, value: string): void {
  if (!sentryInitialized || !sentryModule) return;
  sentryModule.setTag(key, value);
}

/** Last Sentry event id in this session (for support report correlation). */
export async function getSentryLastEventId(): Promise<string | undefined> {
  if (!sentryInitialized) return undefined;
  const Sentry = sentryModule ?? (await loadSentryModule());
  if (!Sentry) return undefined;
  const id = Sentry.lastEventId();
  return id && id.length > 0 ? id : undefined;
}
